import { Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { Icon } from '@/components/ui/icon';

export interface Step<T extends string = string> {
  /** Stable key — React's list identity, and what `onJump` reports. */
  id: T;
  label: string;
}

/**
 * The step indicator the wizard frames draw: numbered discs joined by a rule,
 * done ones ticked, the current one accented, the rest muted.
 *
 * An `<ol>` because the steps are ordered; the connectors and the tick are
 * decoration and `aria-current="step"` carries the position.
 *
 * `onJump` is the switch between the two wizards that drew this. Given one, a
 * *cleared* step is a button and a step still ahead is a disabled one — which is
 * also what tells a reader them apart. Omitted, nothing is clickable: the device
 * wizard's last step has already issued a one-time code, so a jump back would
 * offer to re-do something that cannot be re-done. Forward jumps are refused
 * either way; going round a guard is how a wizard ships a menu with no name.
 *
 * Below `sm` the steps wrap into a block of chips. `flex-1` at 320px would
 * divide the row between four of them and overlap every label with the next
 * disc. From `sm` up they share the row and the connector grows into the slack.
 *
 * `letter-spacing` is deliberately absent: the labels are translated, and
 * Arabic letterforms join, so tracking on them breaks the joins.
 */
export function Stepper<T extends string>({
  steps,
  current,
  onJump,
}: {
  steps: readonly Step<T>[];
  /** Index of the step the operator is on. */
  current: number;
  /** Supplied only by a wizard whose earlier steps may be revisited. */
  onJump?: (id: T) => void;
}) {
  const { t } = useTranslation();

  return (
    <ol
      className="flex flex-wrap items-center gap-x-3 gap-y-1"
      aria-label={t('common.steps')}
    >
      {steps.map((step, index) => {
        const done = index < current;
        const active = index === current;
        const body = (
          <>
            <span
              className={cn(
                'grid h-6 w-6 shrink-0 place-items-center rounded-full border text-xs',
                active && 'border-accent-solid bg-accent-solid text-accent-on font-medium',
                done && 'border-green-600 text-green-600 dark:text-green-400',
                !active && !done && 'border-line-strong text-ink-muted',
              )}
            >
              {done ? <Icon as={Check} size="sm" aria-hidden /> : index + 1}
            </span>
            <span
              className={cn(
                'truncate text-sm',
                active ? 'font-medium text-accent' : done ? 'text-ink' : 'text-ink-muted',
              )}
            >
              {step.label}
            </span>
          </>
        );

        return (
          <li key={step.id} className="flex items-center gap-2 sm:min-w-0 sm:flex-1">
            {onJump ? (
              <button
                type="button"
                disabled={!done}
                onClick={() => onJump(step.id)}
                aria-current={active ? 'step' : undefined}
                className={cn(
                  'flex min-w-0 shrink-0 items-center gap-2 rounded-lg px-1 py-0.5',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  done && 'hover:bg-hover',
                  'disabled:cursor-default',
                )}
              >
                {body}
              </button>
            ) : (
              <span
                aria-current={active ? 'step' : undefined}
                className="flex min-w-0 shrink-0 items-center gap-2"
              >
                {body}
              </span>
            )}
            {index < steps.length - 1 && (
              <span aria-hidden className="hidden h-px min-w-4 flex-1 bg-line sm:block" />
            )}
          </li>
        );
      })}
    </ol>
  );
}
